import GrayCard from "components/gray-card";
import DragNDrop from "components/inputs/dragNdrop";
import Radio from "components/radio";
import Typography from "components/typography";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { FormatAudioDuration } from "utils/dateConvert";
import extractFrameAsFile from "utils/extractFrameAsFile";

interface IProps {
  videoFile: File | undefined;
  setFile: (file: File | undefined) => void;
  imageFile: File | undefined;
}
const ThumbnailImage = ({ videoFile, setFile, imageFile }: IProps) => {
  const [radio, setRadio] = useState("random");
  const [duration, setDuration] = useState(0);
  const [input, setInput] = useState("0");

  const handleLoadedMetadata = (event: any) => {
    setDuration(event.target.duration);
  };

  useEffect(() => {
    const GetThumbnail = async () => {
      if (!videoFile) {
        return;
      }
      try {
        let frame;
        if (radio === "random") {
          frame = await extractFrameAsFile(videoFile, 0);
        } else {
          frame = await extractFrameAsFile(
            videoFile,
            (Number(input) / 100) * duration
          );
        }
        setFile(frame);
      } catch (e) {}
    };

    GetThumbnail();
  }, [radio, input, videoFile, duration]);

  return (
    <GrayCard title="Thumbnail Image">
      {videoFile && (
        <video
          onLoadedMetadata={handleLoadedMetadata}
          style={{ display: "none" }}
          // ref={audioRef}
        >
          <source src={URL.createObjectURL(videoFile)} type="video/mp4" />
          Your browser does not support the audio element.
        </video>
      )}

      <RadioItem>
        <Radio
          value={radio === "select"}
          onClick={() => {
            setRadio("select");
          }}
        />
        <Typography>
          Select the exact point in your video where you want your thumbnail to
          start
        </Typography>
      </RadioItem>
      <RadioItem>
        <Radio
          value={radio === "upload"}
          onClick={() => {
            setRadio("upload");
          }}
        />
        <Typography>Upload custom image for your thumbnail</Typography>
      </RadioItem>
      <RadioItem>
        <Radio
          value={radio === "random"}
          onClick={() => {
            setRadio("random");
          }}
        />
        <Typography>
          Let us generate a thumbnail from a specific frame of the video
        </Typography>
      </RadioItem>
      {radio === "select" ? (
        <>
          <RangeContainer>
            <SelectedTimeContainer>
              <Typography size="xs" color={Colors.yellow100}>
                {FormatAudioDuration((Number(input) / 100) * duration)}
              </Typography>
            </SelectedTimeContainer>
            <DurationContainer>
              <Typography size="xs" color={Colors.yellow100}>
                {FormatAudioDuration(duration)}
              </Typography>
            </DurationContainer>
            <RangeInput
              min="0"
              max={"100"}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </RangeContainer>
          <InfoDiv>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M11 6C11 8.7614 8.7614 11 6 11C3.23857 11 1 8.7614 1 6C1 3.23857 3.23857 1 6 1C8.7614 1 11 3.23857 11 6ZM6 8.875C6.2071 8.875 6.375 8.7071 6.375 8.5V5.5C6.375 5.2929 6.2071 5.125 6 5.125C5.7929 5.125 5.625 5.2929 5.625 5.5V8.5C5.625 8.7071 5.7929 8.875 6 8.875ZM6 3.5C6.27615 3.5 6.5 3.72386 6.5 4C6.5 4.27614 6.27615 4.5 6 4.5C5.72385 4.5 5.5 4.27614 5.5 4C5.5 3.72386 5.72385 3.5 6 3.5Z"
                fill="#909090"
              />
            </svg>
            <Typography size="s">
              Drag the bar to select thumbnail image from a time frame in your
              video.
            </Typography>
          </InfoDiv>
        </>
      ) : radio === "upload" ? (
        <UploadThumbnailArea>
          <DragDropArea>
            {imageFile ? (
              <>
                <CancelIcon onClick={() => setFile(undefined)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10ZM6.96963 6.96965C7.26252 6.67676 7.73739 6.67676 8.0303 6.96965L10 8.9393L11.9696 6.96967C12.2625 6.67678 12.7374 6.67678 13.0303 6.96967C13.3232 7.26256 13.3232 7.73744 13.0303 8.0303L11.0606 10L13.0303 11.9696C13.3232 12.2625 13.3232 12.7374 13.0303 13.0303C12.7374 13.3232 12.2625 13.3232 11.9696 13.0303L10 11.0607L8.0303 13.0303C7.73742 13.3232 7.26254 13.3232 6.96965 13.0303C6.67676 12.7374 6.67676 12.2625 6.96965 11.9697L8.9393 10L6.96963 8.0303C6.67673 7.73742 6.67673 7.26254 6.96963 6.96965Z"
                      fill="#CDCDCD"
                    />
                  </svg>
                </CancelIcon>

                <UploadedThumbnailImage
                  src={URL.createObjectURL(imageFile)}
                  alt="thumbnail"
                />
              </>
            ) : (
              <DragNDrop
                setFile={(file) => {
                  setFile(file);
                }}
                type="image"
                limit={10}
              />
            )}
          </DragDropArea>
          <UploadTextArea>
            <Typography size="s" weight="semibold" mb="0.8">
              Thumbnail Requirements
            </Typography>
            <UploadList>
              <li>
                <Typography size="xs">
                  Image must be a .jpg or .png file
                </Typography>
              </li>{" "}
              <li>
                <Typography size="xs">
                  Maximum file size of around 10MB
                </Typography>
              </li>{" "}
              <li>
                <Typography size="xs">
                  Thumbnail must be 1920 x 1080 px (landscape)
                </Typography>
              </li>{" "}
              <li>
                <Typography size="xs">
                  Don’t upload blurry or pixelated thumbnails
                </Typography>
              </li>
              <li>
                <Typography size="xs">
                  image must not contain website addresses, brand logos, social
                  media handles or logos, any text except artists names and/or
                  name of release
                </Typography>
              </li>
            </UploadList>
            <Typography size="xs" color={Colors.grey500}>
              If it contains any of the above, your music video will be rejected
              by stores
            </Typography>
          </UploadTextArea>
        </UploadThumbnailArea>
      ) : null}
    </GrayCard>
  );
};

export default ThumbnailImage;

const RadioItem = styled.div`
  display: grid;
  grid-template-columns: 3% 96%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.4rem;
  @media only screen and (max-width: 769px) {
    grid-template-columns: 3% 94%;
  }
`;

const InfoDiv = styled.div`
  display: grid;
  grid-template-columns: 3% 97%;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: ${Colors.grey500};
  @media only screen and (max-width: 769px) {
    grid-template-columns: 5% 94%;
  }
`;

const RangeContainer = styled.div`
  width: 100%;
  margin-bottom: 2.4rem;
  position: relative;
  padding-top: 1.5rem;
`;
const RangeInput = styled.input.attrs({ type: "range" })`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  border-radius: 1rem;
  height: 0.8rem;
  -webkit-transition: 0.2s;
  background: ${(props) => `linear-gradient(
    to right,
    ${Colors.yellow400} ${props.value}%,
    rgba(254, 198, 103, 0.1) ${props.value}%)`};

  transition: background 450ms ease-in;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 1.2rem;
    height: 1.2rem;
    background: white;
    cursor: pointer;
    border-radius: 50%;
  }

  &::-moz-range-thumb {
    width: 1.2rem;
    height: 1.2rem;
    background: white;
    cursor: pointer;
    border-radius: 50%;
  }

  &::-moz-range-progress {
    background-color: #43e5f7;
  }
`;

const SelectedTimeContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const DurationContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const UploadThumbnailArea = styled.div`
  display: grid;
  grid-template-columns: 50% 40%;
  align-items: start;
  justify-content: space-between;
  position: relative;
  @media only screen and (max-width: 769px) {
    grid-template-columns: 100%;
    grid-row-gap: 2.4rem;
  }
`;

const DragDropArea = styled.div`
  width: 100%;
  height: 24.6rem;
  position: relative;
  @media only screen and (max-width: 769px) {
    height: 18.2rem;
  }
`;

const UploadedThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.8rem;
`;
const UploadTextArea = styled.div`
  width: 75%;
`;

const UploadList = styled.ul`
  padding-left: 1.5rem;
  color: ${Colors.grey500};
  padding-bottom: 0;

  li {
    margin-bottom: 0.4rem;
  }
`;

const CancelIcon = styled.div`
  cursor: pointer;
  position: absolute;
  top: -1.2rem;
  right: -1.2rem;
  width: 2.4rem;
  height: 2.4rem;
`;
